import { FormControl } from '@angular/forms';
import { debounceTime, distinctUntilChanged, map, Observable } from 'rxjs';

import { CodeValue } from '../../../../../../shared/models/code-value';
import { normalizeSearchString } from '../../../../../../shared/utils/string-operation';

import { FieldAtomic, FieldValueObject, FieldValue, FieldType, addFieldValue } from './field';

export class FieldTextCst extends FieldAtomic<string> {
	private aValue: string | undefined;

	constructor(
		name: string,
		originalValue: string | undefined,
		value: string | undefined
	) {
		super(false, 'textCst', name, originalValue);
		this.aValue = value;
	}

	value() {
		return this.aValue;
	}

	collectVarValue() {
		// noop
	}

	setValue(value: FieldValue) {
		if (value === undefined || typeof value === 'string') {
			this.aValue = value;
			return;
		}
		throw new Error(`unable to set value; expected string, but found ${ JSON.stringify(value) }`);
	}
}

abstract class AbstractFieldTextVar extends FieldAtomic<string> {
	readonly path: string;
	readonly control: FormControl;
	constructor(
		type: FieldType,
		name: string,
		path: string,
		originalValue: string | undefined,
		value: string | undefined
	) {
		super(true, type, name, originalValue);
		this.path = path;
		this.control = new FormControl(value);
	}

	collectVarValue(object: FieldValueObject) {
		addFieldValue(this.path, this.value(), object);
	}

	setValue(value: FieldValue) {
		if (value === undefined || typeof value === 'string') {
			this.control.setValue(value);
			return;
		}
		throw new Error(`unable to set value; expected string, but found ${ JSON.stringify(value) }`);
	}
}

export class FieldTextVar extends AbstractFieldTextVar {
	readonly maxLength: number;

	constructor(
		name: string,
		path: string,
		originalValue: string | undefined,
		value: string | undefined,
		maxLength: number
	) {
		super('textVar', name, path, originalValue, value);
		this.maxLength = maxLength;
	}

	value() {
		return <string | undefined> this.control.value;
	}
}

interface SelectionValues {
	readonly name: string;
	readonly values: (string | CodeValue)[];
}

export class FieldTextSelection extends AbstractFieldTextVar {
	static RETRIEVE_CODE_FROM_VALUE(
		value: string | undefined,
		availableValues: SelectionValues[]
	) {
		if (value === undefined) {
			return undefined;
		}
		for (const group of availableValues) {
			for (const codeValue of group.values) {
				if (typeof codeValue === 'string') {
					if (codeValue === value) {
						return codeValue;
					}
				} else if (codeValue.value === value) {
					return codeValue.code;
				}
			}
		}
		return undefined;
	}

	readonly availableValues: SelectionValues[];
	readonly filteredValues: Observable<SelectionValues[]>;

	constructor(
		name: string,
		path: string,
		originalValue: string | undefined,
		value: string | undefined,
		availableValues: SelectionValues[]
	) {
		super(
			'textSelection',
			name,
			path,
			originalValue,
			value
		);
		this.availableValues = availableValues;
		this.control.setValue(value);
		this.filteredValues = this.control.valueChanges.pipe(
			debounceTime(500),
			distinctUntilChanged(),
			map(x => this.filterValues(x === null || x === undefined ? undefined : <string> x))
		);
	}

	code() {
		const value = this.control.value === null ? undefined : <CodeValue | string | undefined> this.control.value;
		return value === undefined ? undefined : typeof value === 'string' ? value : value.code;
	}

	override collectVarValue(object: FieldValueObject) {
		addFieldValue(
			this.path,
			FieldTextSelection.RETRIEVE_CODE_FROM_VALUE(this.value(), this.availableValues),
			object
		);
	}

	value() {
		const value = this.control.value === null ? undefined : <string | undefined> this.control.value;
		return value === undefined ? undefined : value;
	}

	private filterValues(filterValue: string | undefined) {
		if (filterValue === undefined || filterValue === '') {
			return this.availableValues;
		}
		const normalizedFilterValue = normalizeSearchString(filterValue);
		return this.availableValues.map(
			group => ({
				name: group.name,
				values: group.values.filter(x =>
					typeof x === 'string'
						? x.toLowerCase().includes(filterValue.toLowerCase())
						: normalizeSearchString(x.value).includes(normalizedFilterValue))
			})
		)
			.filter(group => group.values.length > 0);
	}
}
